import { useMemo } from "react";
import { Chart } from "primereact/chart";
import { chartColors } from "sj-ui-toolkit/src/atlas/utils";
import type { AssignmentProgressCounts } from "@/helpers/assignmentHelpers";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AssignmentProgressChartProps {
  progress: AssignmentProgressCounts;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Doughnut breakdown of assigned users by completion status, with a text legend for a11y. */
export const AssignmentProgressChart = ({ progress }: AssignmentProgressChartProps) => {
  const segments = useMemo(() => {
    const [dojo1, dojo2, dojo3] = chartColors.getDojoChartColors();
    return [
      {
        label: "Not Started",
        value: progress.notStarted,
        color: chartColors.getLessonChartColor(),
      },
      { label: "In Progress", value: progress.inProgress, color: dojo1 },
      { label: "Completed", value: progress.complete, color: dojo2 },
      { label: "Paused", value: progress.abandoned, color: dojo3 },
    ];
  }, [progress]);

  const chartData = useMemo(
    () => ({
      labels: segments.map(s => s.label),
      datasets: [
        {
          data: segments.map(s => s.value),
          backgroundColor: segments.map(s => s.color),
          borderWidth: 0,
        },
      ],
    }),
    [segments]
  );

  const chartOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            // Suppress the default title — it just repeats the label
            title: () => "",
            label: (ctx: { label?: string; parsed: number }) => {
              const percent =
                progress.total > 0 ? Math.round((ctx.parsed / progress.total) * 100) : 0;
              return `${ctx.label ?? ""}: ${ctx.parsed} (${percent}%)`;
            },
          },
        },
      },
      cutout: "70%",
    }),
    [progress.total]
  );

  if (progress.total === 0) {
    return <p className="text-secondary">No users assigned yet.</p>;
  }

  return (
    <div className="flex items-center justify-center gap-6">
      <Chart
        type="doughnut"
        data={chartData}
        options={chartOptions}
        style={{ width: "320px", height: "320px" }}
      />
      <ul className="flex flex-col gap-2">
        {segments.map(s => (
          <li key={s.label} className="flex items-center gap-2">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ backgroundColor: s.color }}
              aria-hidden="true"
            />
            <span>
              {s.label}: {s.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
